import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Icon, IconButton, Select, Switch, useConfirm } from "@jkast90/kast-common/ui";
import { cloudSaves } from "../emulator/cloudSaves";
import { admin, signedInAdmin } from "../library/admin";
import { useAdminMode } from "@common/adminMode";
import { hasGroup, useSession, useSignedIn } from "@jkast90/kast-common/auth";
import { ADMIN_GROUPS, signIn, signOut } from "../auth/session";
import { useOnline } from "@jkast90/kast-common/offline";
import Cover from "../library/Cover";
import { ago, lastActive, playerName, type UserSummary } from "../library/dashboard";
import { SYSTEM_LABEL, type System } from "../library/localLibrary";
import { formatPlayed } from "../library/playtime";
import { errorText, queryKeys, saveKeys, useDashboard } from "../library/queries";
import { saveMyProfile } from "../library/profile";
import { displayName, romBaseName } from "../emulator/saves";

// A player's profile: who they are, how much they've played and of what,
// and what they have saved in the cloud. Yours by default; anyone in the
// family from the selector, since the dashboard feed has everyone.
export default function ProfilePage() {
  const { user } = useSession();
  const isAuthenticated = useSignedIn();
  const online = useOnline();
  const userEmail = user?.email || null;
  // The name claim only: user.name falls back to the email, shown beside it.
  const userName = typeof user?.claims.name === "string" ? user.claims.name.trim() || null : null;
  const { sub: me } = signedInAdmin();
  // Deleting someone else's saves is an admin tool, offered only while admin tools are on.
  const adminMode = useAdminMode();
  const isAdmin = adminMode.unlocked;
  const [confirm, confirmDialog] = useConfirm();
  const [deleting, setDeleting] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const feed = useDashboard(isAuthenticated);

  // You first, then everyone else by name.
  const players = useMemo(() => {
    const all = [...(feed.data?.players ?? [])];
    all.sort((a, b) => (a.sub === me ? -1 : b.sub === me ? 1 : playerName(a).localeCompare(playerName(b))));
    if (me && !all.some((p) => p.sub === me)) {
      all.unshift({ sub: me, email: userEmail, name: userName, lastSave: null, games: [], played: [], secondsPlayed: 0, lastPlayed: null, picture: null });
    }
    return all;
  }, [feed.data, me, userEmail, userName]);
  const chosenSub = params.get("user") ?? me;
  const player: UserSummary | null = players.find((p) => p.sub === chosenSub) ?? players[0] ?? null;
  const isMe = player?.sub === me;
  const choose = (sub: string) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (sub === me) next.delete("user");
      else next.set("user", sub);
      return next;
    });

  const played = [...(player?.played ?? [])].sort((a, b) => b.lastPlayed.localeCompare(a.lastPlayed));
  // The picture: the game they chose, else the one they've played most.
  const mostPlayed = [...(player?.played ?? [])].sort((a, b) => b.seconds - a.seconds)[0];
  const pictureGame = (player?.picture && player.played.find((g) => g.game === player.picture)) || mostPlayed || null;
  const [choosing, setChoosing] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const queryClient = useQueryClient();
  // Removes a game's cloud saves (the in-game save and its slots): your own
  // through the saves API, anyone's as an admin. Copies on devices stay.
  const deleteCloudSaves = async (game: string, states: number, battery: boolean) => {
    if (!player) return;
    const what = [battery && "the in-game save", states > 0 && `${states} slot${states === 1 ? "" : "s"}`].filter(Boolean).join(" and ");
    const ok = await confirm({
      title: `Delete ${displayName(game)}'s cloud saves?`,
      message: `${what.charAt(0).toUpperCase()}${what.slice(1)} ${isMe ? "you" : name} saved in the cloud ${states + (battery ? 1 : 0) === 1 ? "is" : "are"} removed. Copies on devices stay, and go back up from there. This can't be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    setDeleting(game);
    setSaveError(null);
    try {
      const names = (isMe ? await cloudSaves.list() : await admin.saves(player.sub)).map((s) => s.name).filter((n) => romBaseName(n) === game);
      for (const file of names) await (isMe ? cloudSaves.remove(file) : admin.deleteSave(player.sub, file));
      await Promise.all(saveKeys(player.sub, isMe).map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Could not delete the saves.");
    } finally {
      setDeleting(null);
    }
  };

  const choosePicture = async (game: string | null) => {
    setSaving(game ?? "");
    try {
      await saveMyProfile({ picture: game });
      await queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      setChoosing(false);
    } catch {
      // The feed keeps the old picture; the dialog stays open to try again.
    } finally {
      setSaving(null);
    }
  };
  const total = player?.secondsPlayed ?? 0;
  const error = feed.error ? errorText(feed.error, "Could not load profiles.") : null;
  const name = player ? (isMe ? "You" : playerName(player)) : "";
  const who = isMe ? "you" : name;

  return (
    <div className="about-main">
      <div className="library-head profile-head">
        <h1>Profile</h1>
        {isAuthenticated && (
          <span className="profile-head-actions">
            <IconButton className="btn icon-btn" label="Sign out" onClick={() => void signOut()}><Icon name="logout" size={20} className="icon" /></IconButton>
            {players.length > 1 && (
              <Select
                variant="bare"
                value={player?.sub ?? ""}
                options={players.map((p) => ({ value: p.sub, label: p.sub === me ? "You" : playerName(p) }))}
                onChange={choose}
                aria-label="Whose profile"
                className="dropdown profile-picker"
              />
            )}
          </span>
        )}
      </div>

      {error && <p className="notice error">{error}</p>}
      {confirmDialog}
      {choosing && player && (
        <div className="modal-overlay" onClick={() => setChoosing(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Your picture</h2>
              <button type="button" className="btn icon-btn" onClick={() => setChoosing(false)} aria-label="Close">
                <Icon name="close" size={20} className="icon" />
              </button>
            </div>
            {player.played.length === 0 ? (
              <p className="muted small">Play a game while signed in and its cover can be your picture.</p>
            ) : (
              <>
                <p className="muted small">The cover of one of your games. Without a choice it's the one you've played most.</p>
                <ul className="picture-grid">
                  {[...player.played]
                    .sort((a, b) => b.seconds - a.seconds)
                    .map((game) => (
                      <li key={game.game}>
                        <button
                          type="button"
                          className={`picture-option${player.picture === game.game ? " selected" : ""}`}
                          disabled={saving !== null}
                          onClick={() => choosePicture(game.game)}
                          aria-label={displayName(game.name)}
                          data-tip={displayName(game.name)}
                        >
                          {saving === game.game ? <span className="spinner" aria-hidden="true" /> : <Cover name={game.name} className="picture-option-cover" />}
                        </button>
                      </li>
                    ))}
                </ul>
                {player.picture && (
                  <div className="modal-footer">
                    <button type="button" className="btn" disabled={saving !== null} onClick={() => choosePicture(null)}>
                      Back to most played
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {!isAuthenticated ? (
        <section className="panel">
          <h2>Account</h2>
          <p>Sign in to see your play time, your games and your cloud saves, and everyone else's.</p>
          <button type="button" className="btn primary" disabled={!online} onClick={signIn}>
            {online ? "Sign in" : "Signing in needs a connection"}
          </button>
        </section>
      ) : !player ? (
        <p className="muted">{feed.isLoading ? "Loading…" : "Nobody here yet."}</p>
      ) : (
        <>
          <section className="panel">
            <h2>Account</h2>
            <div className="profile-account">
              <button
                type="button"
                className={`profile-picture${isMe ? " mine" : ""}`}
                disabled={!isMe}
                aria-label={isMe ? "Choose a picture" : undefined}
                data-tip={isMe ? "Choose a picture" : pictureGame ? displayName(pictureGame.name) : undefined}
                onClick={() => setChoosing(true)}
              >
                {pictureGame ? <Cover name={pictureGame.name} className="profile-picture-cover" /> : <Icon name="videogame_asset" size={20} className="icon" />}
                {isMe && (
                  <span className="profile-picture-edit">
                    <Icon name="edit" size={20} className="icon" />
                  </span>
                )}
              </button>
              <div>
                <strong>{(isMe ? (userName ?? player.name) : player.name) ?? (isMe ? userEmail : (player.email ?? playerName(player)))}</strong>
                <span className="muted small">
                  {(isMe ? (userName ?? player.name) : player.name) && (isMe ? userEmail : player.email) ? `${isMe ? userEmail : player.email} · ` : ""}
                  kast-dev account{isMe && hasGroup(user, ADMIN_GROUPS) ? " · CloudBoy admin" : ""}
                </span>
                {isMe && adminMode.isAdmin && (
                  <Switch
                    size="sm"
                    wrapperClassName="controls-switch"
                    label="Admin tools on this device"
                    checked={adminMode.unlocked}
                    onChange={adminMode.setEnabled}
                  />
                )}
                <span className="muted small">
                  {[player.lastPlayed && `last played ${ago(player.lastPlayed)}`, player.lastSave && `last saved ${ago(player.lastSave)}`]
                    .filter(Boolean)
                    .join(" · ") || "no activity yet"}
                </span>
              </div>
              <div className="library-stats profile-stats">
                <div>
                  <span className="muted small">Played</span>
                  <strong>{total ? formatPlayed(total) : "None yet"}</strong>
                </div>
                <div>
                  <span className="muted small">Games</span>
                  <strong>{played.length}</strong>
                </div>
                <div>
                  <span className="muted small">Last active</span>
                  <strong>{lastActive(player) ? ago(lastActive(player)!) : "Never"}</strong>
                </div>
              </div>
            </div>
          </section>

          <section className="panel">
            <h2>Games</h2>
            {played.length === 0 ? (
              <p className="muted small">
                {isMe ? "Time shows here once you play while signed in." : `${name} hasn't played while signed in yet.`}
              </p>
            ) : (
              <ol className="rank-list">
                {played.map((game) => (
                  <li key={game.game}>
                    <Link to={`/library?${new URLSearchParams({ game: romBaseName(game.name) })}`} className="rank-row">
                      <Cover name={game.name} className="rank-cover" />
                      <span className="rank-text">
                        <strong>{displayName(game.name)}</strong>
                        <span className="muted small">{SYSTEM_LABEL[game.system as System] ?? game.system}</span>
                      </span>
                      <span className="rank-time profile-game-time">
                        <strong>{formatPlayed(game.seconds)}</strong>
                        <span className="muted small">played {ago(game.lastPlayed)}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </section>

          <section className="panel">
            <h2>Cloud saves</h2>
            {player.games.length === 0 ? (
              <p className="muted small">
                {isMe ? "No saves in the cloud yet. They go up as you play while signed in." : `No cloud saves from ${who} yet.`}
              </p>
            ) : (
              <>
                <p className="muted small">
                  {player.games.length} {player.games.length === 1 ? "game" : "games"}
                  {player.lastSave && ` · last saved ${ago(player.lastSave)}`}
                </p>
                {saveError && <p className="notice error">{saveError}</p>}
                <ul className="profile-saves">
                  {player.games.map((game) => (
                    <li key={game.game}>
                      <Link to={`/library?${new URLSearchParams({ game: game.game })}`} className="profile-save-game">
                        <Cover name={game.game} className="rank-cover" />
                        {displayName(game.game)}
                      </Link>
                      <span className="profile-save-meta">
                        <span className="muted small">
                          {[game.hasBatterySave && "in-game save", game.states > 0 && `${game.states} slot${game.states === 1 ? "" : "s"}`]
                            .filter(Boolean)
                            .join(" · ")}{" "}
                          · {ago(game.lastSave)}
                        </span>
                        {(isMe || isAdmin) && (
                          <IconButton
                            label="Delete these cloud saves"
                            className="btn icon-btn danger"
                            disabled={deleting !== null}
                            onClick={() => deleteCloudSaves(game.game, game.states, game.hasBatterySave)}
                          >
                            <Icon name="delete" size={20} className="icon" />
                          </IconButton>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}
